import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, Copy, Trash2, CheckCircle2, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminOrganizers() {
  const [links, setLinks] = useState([]);
  const [events, setEvents] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const [l, e] = await Promise.all([
      api.get("/admin/organizer-links"),
      api.get("/admin/events"),
    ]);
    setLinks(l.data);
    setEvents(e.data);
  };
  useEffect(() => { load(); }, []);

  const copyLink = async (link) => {
    const url = `${window.location.origin}${link.setup_url}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        toast.success("Setup link copied");
        return;
      }
    } catch {
      // fall through to fallback
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Setup link copied");
    } catch {
      toast.error("Copy failed — please copy manually");
      window.prompt("Copy this link:", url);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this link? Anyone holding it will lose access.")) return;
    await api.delete(`/admin/organizer-links/${id}`);
    toast.success("Link deleted");
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 md:py-12 max-w-7xl">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="kicker mb-2">ORGANIZER LINKS</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tighter">Grant event access</h1>
          <p className="mt-2 text-sm text-[#475569]">Generate secure setup links so organizers can upload photos for assigned events.</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 bg-[#5B4CFF] hover:bg-[#4a3dde] text-white font-semibold px-5 py-3 text-sm"
          data-testid="new-link-btn"
        >
          <Plus size={14} /> New organizer link
        </button>
      </div>

      {links.length === 0 ? (
        <div className="border border-dashed border-[#E2E8F0] p-12 text-center bg-white" data-testid="links-empty">
          <p className="font-display text-xl text-[#0F172A] mb-2">No organizer links yet</p>
          <p className="text-sm text-[#475569]">Generate one to invite an event organizer.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0]" data-testid="links-table">
          {links.map((link) => (
            <div key={link.id} className="grid grid-cols-1 md:grid-cols-12 px-6 py-4 border-b last:border-b-0 border-[#E2E8F0] items-center gap-4">
              <div className="md:col-span-4">
                <p className="font-display text-base font-semibold text-[#0F172A]">{link.event_name}</p>
                <p className="text-xs text-[#475569] mt-1">{link.email || "Any organizer"}</p>
              </div>
              <div className="md:col-span-4">
                <p className="font-mono text-[11px] text-[#475569] truncate" data-testid={`link-token-${link.id}`}>
                  {window.location.origin}{link.setup_url}
                </p>
                <p className="text-xs text-[#475569] mt-1">Expires {link.expires_at?.split("T")[0]}</p>
              </div>
              <div className="md:col-span-2">
                {link.used ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#10B981]">
                    <CheckCircle2 size={12} /> Used
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#5B4CFF]">
                    <Link2 size={12} /> Active
                  </span>
                )}
              </div>
              <div className="md:col-span-2 flex md:justify-end gap-2">
                <button onClick={() => copyLink(link)} className="text-xs font-semibold inline-flex items-center gap-1 border border-[#E2E8F0] hover:border-[#5B4CFF] px-3 py-1.5" data-testid={`copy-link-${link.id}`}>
                  <Copy size={12} /> Copy
                </button>
                <button onClick={() => remove(link.id)} className="text-[#EF4444] hover:bg-[#fef2f2] p-1.5 border border-[#E2E8F0]" data-testid={`del-link-${link.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewLinkModal events={events} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}

function NewLinkModal({ events, onClose, onCreated }) {
  const [eventId, setEventId] = useState(events[0]?.id || "");
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await api.post("/admin/organizer-links", {
        event_id: eventId,
        email: email || null,
        expires_in_days: Number(days),
      });
      const url = `${window.location.origin}${res.data.setup_url}`;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        }
      } catch {
        // ignore — link is still visible in the list
      }
      toast.success("Link created & copied");
      onCreated();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Create failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="new-link-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[#E2E8F0] w-full max-w-md p-8"
      >
        <p className="kicker mb-2">NEW LINK</p>
        <h2 className="font-display text-2xl font-bold text-[#0F172A] mb-6">Invite an organizer</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">EVENT</label>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
              data-testid="nl-event-select"
            >
              {events.length === 0 && <option value="">No events available</option>}
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">EMAIL (OPTIONAL)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
              placeholder="organizer@race.com"
              data-testid="nl-email-input"
            />
          </div>
          <div>
            <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">EXPIRES IN (DAYS)</label>
            <input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
              data-testid="nl-days-input"
            />
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F172A] hover:bg-[#F8FAFC] px-5 py-2.5">Cancel</button>
          <button type="submit" disabled={loading || !eventId} className="bg-[#5B4CFF] hover:bg-[#4a3dde] disabled:opacity-60 text-white font-semibold px-5 py-2.5 text-sm" data-testid="nl-submit">
            {loading ? "Creating…" : "Generate link"}
          </button>
        </div>
      </form>
    </div>
  );
}
